import React, { useState, useEffect } from 'react';
import { userAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import {
  Trophy, Gift, Wallet, Target, CheckCircle,
  XCircle, Clock, Euro, CreditCard, Info, ChevronRight,
  Medal, Award, Share2, Copy
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../lib/apiErrors';
import { shareLink, referralLink, myReferralCode } from '../../lib/share';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';

export function RewardsPage() {
  const [loading, setLoading] = useState(true);
  const [rewardsInfo, setRewardsInfo] = useState(null);
  const [myStats, setMyStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bankAccount, setBankAccount] = useState('');
  const [requestingPayout, setRequestingPayout] = useState(false);
  const { t, i18n } = useTranslation();
  const isSk = String(i18n.language || 'en').startsWith('sk');
  // '1st', '5th-10th' (from the API) -> '1.', '5.–10.' in Slovak
  const placeLabel = (place) => {
    const m = String(place || '').match(/^(\d+)(?:st|nd|rd|th)(?:\s*-\s*(\d+)(?:st|nd|rd|th))?$/i);
    if (!isSk || !m) return place;
    return t('payout.place', { place: m[2] ? `${m[1]}.–${m[2]}.` : `${m[1]}.` });
  };
  const eur = (v) => (isSk ? `${v} €` : `€${v}`);
  const { user } = useAuth();

  const [referralCode, setReferralCode] = useState(user?.referral_code || '');
  useEffect(() => {
    if (!referralCode) myReferralCode(user, userAPI.getReferralStats).then(setReferralCode);
  }, []);
  const inviteLink = referralLink(referralCode);

  const copyInvite = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      toast.success(t('share.copied', 'Copied to clipboard!'));
    } catch (e) {
      toast.error(t('common.copyFailed', 'Copy failed'));
    }
  };

  const shareInvite = async () => {
    const text = t('share.inviteText', 'Join me on SocraQuest — daily quizzes, leagues and real rewards! We both get +100 XP with my link:');
    const res = await shareLink({ title: 'SocraQuest', text, url: inviteLink });
    if (res === 'copied') toast.success(t('share.copied', 'Copied to clipboard!'));
    else if (res === 'failed') toast.info(inviteLink);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [infoRes, statsRes, leaderboardRes] = await Promise.all([
        userAPI.getRewardsInfo(),
        userAPI.getMyRewardsStats(),
        userAPI.getRewardsLeaderboard()
      ]);
      setRewardsInfo(infoRes.data);
      setMyStats(statsRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      toast.error(t('payout.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRequestPayout = async () => {
    if (!bankAccount || bankAccount.length < 10) {
      toast.error(t('apiErrors.invalidIban'));
      return;
    }

    setRequestingPayout(true);
    try {
      const res = await userAPI.requestPayout(bankAccount);
      toast.success(t('payout.submitted'));
      loadData(); // Refresh data
    } catch (error) {
      toast.error(apiErrorMessage(error, t, 'payout.requestFailed'));
    } finally {
      setRequestingPayout(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <LoadingSpinner />
      </div>
    );
  }

  const balance = myStats?.balance || {};
  const eligibility = myStats?.eligibility || {};
  const requirements = eligibility?.requirements || {};

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <motion.h1 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl font-bold text-white mb-2 flex items-center justify-center gap-3"
        >
          <Gift className="w-8 h-8 text-yellow-400" />
          {t('payout.title')}
        </motion.h1>
        <p className="text-slate-400">{t('payout.subtitle')}</p>
      </div>

      {/* Balance Card */}
      <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-teal-600 to-teal-700 rounded-2xl p-6 text-white"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <Wallet className="w-6 h-6" />
            <span className="font-medium">{t('payout.balance')}</span>
          </div>
          <Euro className="w-5 h-5 opacity-75" />
        </div>
        
        <div className="text-4xl font-bold mb-2">
          {eur(balance.pending_balance_eur || 0)}
        </div>
        <p className="text-teal-200 text-sm mb-4">
          {t('payout.totalPaid', { amount: eur(balance.total_paid_eur || 0) })}
        </p>

        {/* Payout Section */}
        {balance.can_request_payout ? (
          <div className="bg-white/10 rounded-xl p-4 space-y-3">
            <Label className="text-white">{t('payout.iban')}</Label>
            <div className="flex gap-2">
              <Input
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder="SK89 1234 5678 9012 3456 7890"
                className="bg-white/20 border-white/30 text-white placeholder:text-white/50"
              />
              <Button 
                onClick={handleRequestPayout}
                disabled={requestingPayout}
                className="bg-yellow-500 hover:bg-yellow-600 text-black font-semibold"
              >
                {requestingPayout ? <LoadingSpinner className="w-4 h-4" /> : t('payout.request')}
              </Button>
            </div>
            <p className="text-xs text-teal-200">
              {t('payout.minInfo', { amount: eur(balance.min_payout_amount) })}
            </p>
          </div>
        ) : (
          <div className="bg-white/10 rounded-xl p-4">
            <p className="text-sm text-teal-200 flex items-center gap-2">
              <Info className="w-4 h-4" />
              {t('payout.minRequired', { amount: eur(balance.min_payout_amount || 20) })}
            </p>
          </div>
        )}
      </motion.div>

      {/* Eligibility Status */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700/50"
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-5 h-5 text-teal-400" />
          {t('payout.eligibility')}
          {eligibility.eligible ? (
            <span className="ml-2 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium">
              {t('payout.eligible')}
            </span>
          ) : (
            <span className="ml-2 px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
              {t('payout.notEligible')}
            </span>
          )}
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Challenges Requirement */}
          <div className={`p-4 rounded-xl border ${
            requirements.challenges?.met 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-slate-700/50 border-slate-600'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <Clock className={`w-5 h-5 ${requirements.challenges?.met ? 'text-green-400' : 'text-slate-400'}`} />
              {requirements.challenges?.met ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
            </div>
            <p className="text-2xl font-bold text-white">
              {requirements.challenges?.current || 0}/{requirements.challenges?.required || 20}
            </p>
            <p className="text-sm text-slate-400">{t('rewardsRules.daysPlayed', 'Days played')}</p>
          </div>

          {/* Success Rate Requirement */}
          <div className={`p-4 rounded-xl border ${
            requirements.success_rate?.met 
              ? 'bg-green-500/10 border-green-500/30' 
              : 'bg-slate-700/50 border-slate-600'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <Target className={`w-5 h-5 ${requirements.success_rate?.met ? 'text-green-400' : 'text-slate-400'}`} />
              {requirements.success_rate?.met ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
            </div>
            <p className="text-2xl font-bold text-white">
              {Number(requirements.success_rate?.current || 0).toFixed(2)}%
            </p>
            <p className="text-sm text-slate-400">{t('payout.successRate', { min: requirements.success_rate?.required || 70 })}</p>
          </div>

        </div>

        <p className="text-xs text-slate-500 mt-4 text-center">
          {t('rewardsRules.requirementsHint', 'Meet both requirements to be eligible for monthly rewards.')}{' '}
          <Link to="/rewards-rules" className="text-teal-400 hover:text-teal-300 underline" data-testid="official-rules-link">
            {t('rewardsRules.link', 'Official rules')}
          </Link>
        </p>
        <p className="text-[11px] text-slate-500 mt-1 text-center">
          {t('rewardsRules.appleNote', 'Apple and Google are not sponsors of, and are not involved in, the rewards program.')}
        </p>

        {/* Invite friends */}
        <div className="mt-5 p-4 rounded-xl bg-teal-500/10 border border-teal-500/25">
          <div className="flex items-center gap-2 mb-2">
            <Gift className="w-4 h-4 text-teal-300" />
            <p className="text-sm font-semibold text-teal-200">{t('share.inviteTitle', 'Invite friends — you both get +100 XP')}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <code className="flex-1 text-xs text-slate-300 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2.5 overflow-x-auto whitespace-nowrap">
              {inviteLink}
            </code>
            <div className="flex gap-2">
              <Button size="sm" onClick={copyInvite} variant="outline"
                className="bg-transparent border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white rounded-lg h-10" data-testid="copy-invite-btn">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> {t('share.copy', 'Copy')}
              </Button>
              <Button size="sm" onClick={shareInvite}
                className="bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold rounded-lg h-10" data-testid="share-invite-btn">
                <Share2 className="w-3.5 h-3.5 mr-1.5" /> {t('share.button', 'Share')}
              </Button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Prize Distribution */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700/50"
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />
          {t('payout.monthlyPrizes')}
        </h2>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {rewardsInfo?.distribution?.map((item, idx) => (
            <div key={idx} className={`p-3 rounded-xl text-center ${
              idx === 0 ? 'bg-yellow-500/20 border border-yellow-500/30' :
              idx === 1 ? 'bg-slate-400/20 border border-slate-400/30' :
              idx === 2 ? 'bg-orange-600/20 border border-orange-600/30' :
              'bg-slate-700/50 border border-slate-600'
            }`}>
              <div className="flex justify-center mb-2">
                {idx === 0 && <Medal className="w-6 h-6 text-yellow-400" />}
                {idx === 1 && <Medal className="w-6 h-6 text-slate-300" />}
                {idx === 2 && <Medal className="w-6 h-6 text-orange-500" />}
                {idx > 2 && <Award className="w-5 h-5 text-slate-400" />}
              </div>
              <p className="text-xs text-slate-400">{placeLabel(item.place)}</p>
              <p className="text-lg font-bold text-white">{eur(item.prize_eur)}</p>
            </div>
          ))}
        </div>

        <div className="mt-4 p-4 bg-slate-700/30 rounded-xl">
          <p className="text-sm text-slate-300 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-teal-400" />
            <span>
              <strong>{t('payout.rulesTitle')}</strong> {t('payout.rulesText', { amount: eur(rewardsInfo?.payout?.min_amount || 20) })}
            </span>
          </p>
        </div>
      </motion.div>

      {/* Top 10 Leaderboard Preview */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="bg-slate-800/50 backdrop-blur rounded-2xl p-6 border border-slate-700/50"
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Trophy className="w-5 h-5 text-teal-400" />
          {t('payout.topPlayers')}
        </h2>

        {leaderboard.length === 0 ? (
          <p className="text-center text-slate-400 py-8">{t('payout.noRankings')}</p>
        ) : (
          <div className="space-y-2">
            {leaderboard.slice(0, 10).map((entry, idx) => (
              <div 
                key={entry.user_id}
                className={`flex items-center justify-between p-3 rounded-xl ${
                  idx === 0 ? 'bg-yellow-500/10 border border-yellow-500/30' :
                  idx === 1 ? 'bg-slate-400/10 border border-slate-400/30' :
                  idx === 2 ? 'bg-orange-600/10 border border-orange-600/30' :
                  'bg-slate-700/30'
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                    idx === 0 ? 'bg-yellow-500 text-black' :
                    idx === 1 ? 'bg-slate-400 text-black' :
                    idx === 2 ? 'bg-orange-600 text-white' :
                    'bg-slate-600 text-white'
                  }`}>
                    {entry.rank}
                  </span>
                  <div>
                    <p className="font-medium text-white">{entry.nickname}</p>
                    <p className="text-xs text-slate-400">{t('payout.percentCorrect', { pct: Number(entry.percentage).toFixed(2) })}</p>
                  </div>
                </div>
                <div className="text-right">
                  {entry.eligible ? (
                    <p className="font-bold text-green-400">{eur(entry.prize_eur)}</p>
                  ) : (
                    <p className="text-xs text-slate-500">{t('payout.notEligibleShort')}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </div>
  );
}
